import { useState, useRef, useEffect } from 'react';

/**
 * Custom hook for multi-stream recording (screen, webcam, audio)
 */
const useRecording = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Refs to store MediaRecorder instances and chunks
  const screenRecorderRef = useRef(null);
  const webcamRecorderRef = useRef(null);
  const audioRecorderRef = useRef(null);

  const screenChunksRef = useRef([]);
  const webcamChunksRef = useRef([]);
  const audioChunksRef = useRef([]);

  const screenStreamRef = useRef(null);
  const webcamStreamRef = useRef(null);
  const audioStreamRef = useRef(null);

  const startTimeRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const autoStopTimeoutRef = useRef(null);

  // Update elapsed time every 100ms while recording
  useEffect(() => {
    if (isRecording) {
      timerIntervalRef.current = setInterval(() => {
        if (startTimeRef.current) {
          const elapsed = Date.now() - startTimeRef.current;
          setElapsedTime(elapsed);
        }
      }, 100);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      setElapsedTime(0);
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isRecording]);

  /**
   * Start recording all streams
   * @param {MediaStream} screenStream - Screen share stream (required)
   * @param {Object} options - Recording options
   * @param {number} options.maxDuration - Maximum duration in milliseconds (optional)
   * @param {Function} options.onAutoStop - Callback to execute when auto-stop is triggered (optional)
   * @returns {Promise<void>}
   */
  const startRecording = async (screenStream, options = {}) => {
    const { maxDuration, onAutoStop } = options;
    try {
      // Reset chunks
      screenChunksRef.current = [];
      webcamChunksRef.current = [];
      audioChunksRef.current = [];

      // Store screen stream
      screenStreamRef.current = screenStream;

      // Get webcam stream
      try {
        const webcamStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        webcamStreamRef.current = webcamStream;
      } catch (err) {
        console.warn('Webcam not available:', err);
        webcamStreamRef.current = null;
      }

      // Get microphone stream
      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
        audioStreamRef.current = audioStream;
      } catch (err) {
        console.warn('Microphone not available:', err);
        audioStreamRef.current = null;
      }

      // Create MediaRecorder for screen
      if (screenStream) {
        screenRecorderRef.current = new MediaRecorder(screenStream, {
          mimeType: 'video/webm;codecs=vp8,opus',
        });

        screenRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            screenChunksRef.current.push(event.data);
          }
        };

        // Start with 10-second timeslice to ensure data is periodically saved
        // This prevents data loss if browser crashes or tab closes during recording
        screenRecorderRef.current.start(10000);
      }

      // Create MediaRecorder for webcam
      if (webcamStreamRef.current) {
        webcamRecorderRef.current = new MediaRecorder(webcamStreamRef.current, {
          mimeType: 'video/webm;codecs=vp8',
        });

        webcamRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            webcamChunksRef.current.push(event.data);
          }
        };

        // Start with 10-second timeslice for periodic data saving
        webcamRecorderRef.current.start(10000);
      }

      // Create MediaRecorder for audio
      if (audioStreamRef.current) {
        audioRecorderRef.current = new MediaRecorder(audioStreamRef.current, {
          mimeType: 'audio/webm;codecs=opus',
        });

        audioRecorderRef.current.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        // Start with 10-second timeslice for periodic data saving
        audioRecorderRef.current.start(10000);
      }

      // Start recording timer
      startTimeRef.current = Date.now();
      setIsRecording(true);
      setError(null);

      // Set up auto-stop if maxDuration is specified
      if (maxDuration && maxDuration > 0) {
        autoStopTimeoutRef.current = setTimeout(() => {
          // Call the onAutoStop callback to let component handle everything
          // The callback (e.g., handleSubmit) will call stopRecording() itself
          // Do NOT call stopRecording() here to avoid race condition
          if (onAutoStop) {
            onAutoStop();
          }
        }, maxDuration);
      }
    } catch (err) {
      setError('Failed to start recording: ' + err.message);
      throw err;
    }
  };

  /**
   * Stop recording and return blob files
   * @returns {Promise<Object>} Object containing screen, webcam, and audio blobs
   */
  const stopRecording = () => {
    return new Promise((resolve) => {
      // Clear auto-stop timeout if it exists
      if (autoStopTimeoutRef.current) {
        clearTimeout(autoStopTimeoutRef.current);
        autoStopTimeoutRef.current = null;
      }

      // Clear timer interval
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      const results = {
        screenBlob: null,
        webcamBlob: null,
        audioBlob: null,
      };

      let stoppedCount = 0;
      const totalRecorders = [
        screenRecorderRef.current,
        webcamRecorderRef.current,
        audioRecorderRef.current,
      ].filter(Boolean).length;

      const checkComplete = () => {
        stoppedCount++;
        if (stoppedCount === totalRecorders) {
          // All recorders stopped, create blobs
          if (screenChunksRef.current.length > 0) {
            results.screenBlob = new Blob(screenChunksRef.current, {
              type: 'video/webm',
            });
          }

          if (webcamChunksRef.current.length > 0) {
            results.webcamBlob = new Blob(webcamChunksRef.current, {
              type: 'video/webm',
            });
          }

          if (audioChunksRef.current.length > 0) {
            results.audioBlob = new Blob(audioChunksRef.current, {
              type: 'audio/webm',
            });
          }

          // Stop all streams
          if (screenStreamRef.current) {
            screenStreamRef.current.getTracks().forEach(track => track.stop());
          }
          if (webcamStreamRef.current) {
            webcamStreamRef.current.getTracks().forEach(track => track.stop());
          }
          if (audioStreamRef.current) {
            audioStreamRef.current.getTracks().forEach(track => track.stop());
          }

          startTimeRef.current = null;
          setIsRecording(false);
          resolve(results);
        }
      };

      // Stop screen recorder
      if (screenRecorderRef.current && screenRecorderRef.current.state !== 'inactive') {
        screenRecorderRef.current.onstop = checkComplete;
        screenRecorderRef.current.stop();
      } else if (screenRecorderRef.current) {
        checkComplete();
      }

      // Stop webcam recorder
      if (webcamRecorderRef.current && webcamRecorderRef.current.state !== 'inactive') {
        webcamRecorderRef.current.onstop = checkComplete;
        webcamRecorderRef.current.stop();
      } else if (webcamRecorderRef.current) {
        checkComplete();
      }

      // Stop audio recorder
      if (audioRecorderRef.current && audioRecorderRef.current.state !== 'inactive') {
        audioRecorderRef.current.onstop = checkComplete;
        audioRecorderRef.current.stop();
      } else if (audioRecorderRef.current) {
        checkComplete();
      }

      // If no recorders were active, resolve immediately
      if (totalRecorders === 0) {
        setIsRecording(false);
        resolve(results);
      }
    });
  };

  return {
    isRecording,
    error,
    elapsedTime,
    startRecording,
    stopRecording,
  };
};

export default useRecording;
